import { useState } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { BiErrorCircle } from "react-icons/bi";
import { MdOutlineMovieCreation, MdOutlineTimer } from "react-icons/md";
import Lottie from "lottie-react";
import { Button } from "MVVM/Components/Button";
import { useUser } from "MVVM/Hooks/useUser";
import { ClipBadges } from "MVVM/Components/ClipBadge";
import { TMoment } from "MVVM/Views/MatchHistory/@types";

import Remixing from "../../../../../../../animations/remixing.json";
import { ClipThumbnail } from "../ClipThumbnail";
import { ReactComponent as RemixIcon } from "../../../../../../../assets/images/badges/remix-icon.svg";
import { MomentClipViewModel } from "./ViewModel";
import {
  ProcessingButton,
  TransitTimeWrapper,
} from "MVVM/Views/MatchHistory/styledComponents";
import { TransitTime } from "MVVM/Components/TransitTime";
import { usePortal } from "MVVM/Hooks/usePortal";
import { QuickEdit } from "MVVM/Components/QuickEdit";
import { analytics } from "MVVM/Analytics";
import { ModalType } from "MVVM/@types";
import { ClipFullModalState } from "MVVM/State/modals";
import { useSetRecoilState } from "recoil";

const MomentClip = ({
  moment,
  outdated,
}: {
  moment: TMoment;
  outdated?: boolean;
}) => {
  const { clip } = moment;
  const { allstarUser } = useUser();
  const history = useHistory();
  const isPruned = clip.status === "PRUNED";
  const [processing, setProcessing] = useState(false);
  const { createPrunedClip } = MomentClipViewModel();
  const setClipFullModalState = useSetRecoilState(ClipFullModalState);
  const {
    portalOpen: quickEditPortalOpen,
    setPortalOpen: setQuickEditPortalOpen,
    Portal: QuickEditPortal,
  } = usePortal(
    <QuickEdit
      momentId={moment.id}
      closeModal={() => setQuickEditPortalOpen(false)}
    />,
    "root",
  );
  const { portalOpen, setPortalOpen, Portal } = usePortal(
    <TransitTimeWrapper>
      <TransitTime user={allstarUser?.user?._id} />
    </TransitTimeWrapper>,
  );
  const handleClick = async () => {
    setProcessing(true);
    createPrunedClip({
      variables: { clipIdentifier: clip.shareId },
    });
  };

  return (
    <>
      {portalOpen && Portal}
      {quickEditPortalOpen && QuickEditPortal}
      <Flex
        flexDirection={["column", "column", "row"]}
        alignItems={["center", "center", "start"]}
        style={{ gap: 12 }}
      >
        {isPruned ? (
          <PrunedThumbnail
            style={{
              backgroundImage: `url(${clip?.thumbnailUrl})`,
            }}
          >
            <DarkOverlay />
            <StyledErrorIcon />
          </PrunedThumbnail>
        ) : (
          <ClipThumbnail clip={clip} />
        )}

        <Flex flexDirection={"column"} flexGrow={1} style={{ gap: 6 }}>
          {isPruned ? (
            <>
              <Text variant={"text.labelSmall"} color="ice">
                This Clip Was Archived
              </Text>
              <PrunedText variant={"text.labelXSmall"} color="chalk">
                Sorry, this clip was archived because no one was watching it.
                You can create it again at any time.
              </PrunedText>
            </>
          ) : (
            <>
              <StyledClipTitle
                variant={"text.labelSmall"}
                onClick={() => {
                  if (clip?.shareId) {
                    setClipFullModalState({
                      isOpen: ModalType.Clip,
                      shareId: clip?.shareId,
                      contextOverride: {
                        type: "match",
                        metadata: {
                          source: "modal",
                        },
                      },
                    });
                  }
                }}
              >
                {clip.title}
              </StyledClipTitle>
              <Flex
                justifyContent="flex-start"
                style={{ gap: 4 }}
                alignItems={"center"}
              >
                <StyledMdOutlineTimer />
                <Text variant={"text.labelXSmall"} color="chalk">
                  {moment.timelineLabel}
                </Text>
              </Flex>
            </>
          )}
          <ClipBadges
            tags={moment.clipTags?.filter(
              (t: { key: string }) =>
                t.key !== "CS_Round" && t.key !== "CS_Weapons",
            )}
            size="sm"
            maxVisible={2}
            expandable
          />
        </Flex>

        {!outdated && (
          <Flex flexShrink={0} alignItems="center">
            {!isPruned ? (
              <RemixButton
                size="tiny"
                classification="roundedFilled"
                flex={true}
                style={{ minWidth: 100, maxWidth: 100 }}
                onClick={() => {
                  setQuickEditPortalOpen(true);
                  analytics.track("Match History League - Remix Button Hit", {
                    userGroup: allstarUser?.user?.userGroup?.type,
                  });
                }}
              >
                <StyledRemixIcon />
                Remix
              </RemixButton>
            ) : processing ? (
              <StyledProcessingButton
                flex={true}
                size="tiny"
                flexShrink={0}
                maxWidth={150}
                onClick={() => setPortalOpen(true)}
              >
                Clip Pending
                <Lottie
                  animationData={Remixing}
                  loop={true}
                  style={{ height: 36, width: 36, minWidth: 36 }}
                />
              </StyledProcessingButton>
            ) : (
              <ClipButton
                size="tiny"
                classification="roundedFilled"
                flex={true}
                onClick={async () => handleClick()}
                onClickCapture={() => {}}
                maxWidth={100}
              >
                <StyledMdOutlineMovieCreation />
                Restore
              </ClipButton>
            )}
          </Flex>
        )}
      </Flex>
    </>
  );
};

const StyledProcessingButton = styled(ProcessingButton)`
  font-size: ${({ theme }) => theme.fontSizes[1]};
`;

const StyledMdOutlineMovieCreation = styled(MdOutlineMovieCreation)`
  color: ${({ theme }) => theme.colors.ice};
  height: 20px;
  margin-right: 6px;
  min-width: 16px;
  width: 20px;
`;

const ClipButton = styled(Button)`
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: 700;
  padding: 16px;

  :disabled {
    cursor: not-allowed;
  }

  :disabled svg {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
  }
`;
const PrunedText = styled(Text)`
  max-width: 250px;
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    max-width: 100%;
  }
`;

const DarkOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  z-index: 1;
`;
const StyledErrorIcon = styled(BiErrorCircle)`
  color: ${({ theme }) => theme.colors.error};
  width: 48px;
  height: 48px;
  z-index: 2;
`;

const StyledClipTitle = styled(Text)`
  transition: color 0.2s;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const StyledMdOutlineTimer = styled(MdOutlineTimer)`
  color: ${({ theme }) => theme.colors.envy};
  height: 15px;
  width: 15px;
  margin-top: 1px;
`;
const RemixButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.midnight};
  background-image: linear-gradient(
    225deg,
    rgba(249, 83, 112, 0.4) 0%,
    rgba(114, 67, 154, 0.4) 100%
  );
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: 700;
  padding: 16px;

  :hover {
    background-color: ${({ theme }) => theme.colors.darkerNed};
  }
`;

const StyledRemixIcon = styled(RemixIcon)`
  height: 20px;
  margin-right: 6px;
  width: 20px;
`;
const ClipDivider = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  height: 1px;
  margin: 4px 0;
  width: 100%;
`;

const PrunedThumbnail = styled(Flex)`
  background-size: cover;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  height: 108px;
  position: relative;
  width: 192px;
  border-radius: 8px;
  flex-shrink: 0;
  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    flex-grow: 1;
    width: 100%;
    aspect-ratio: 16/9;
    height: inherit;
  }
`;

export { MomentClip };
